/**
 * One withheld signal in the curation queue.
 *
 * What this shows is SIGNAL AND SOURCE CONTEXT: the collector-derived summary
 * and interpretation fields, the source system and link, the observation window,
 * the organization, the lineage id, and any prior decision. It is deliberately
 * NOT labelled evidence. The retained source evidence - the captured document in
 * public.source_event_evidence - is not joined by the curation queue view and is
 * not available to this slice, so calling anything here "evidence" would promise
 * a curator something they are not being shown.
 *
 * All text is rendered as text. There is no dangerouslySetInnerHTML, and the
 * source link is rendered only when it survives an http/https check.
 */

import { useState } from 'react';
import type { SubmissionBlock } from '../decision-intent';
import type { CuratorApiError } from '../errors';
import {
  DECISION_LABELS,
  decisionsForState,
  PUBLICATION_STATE_LABELS,
  type CuratorQueueRow as QueueRow,
  type DecisionKind,
} from '../types';
import { CuratorDecisionConfirm } from './CuratorDecisionConfirm';

function Field({ label, value }: { label: string; value: string | null }) {
  if (value === null) return null;
  return (
    <>
      <p className="sx-eyebrow">{label}</p>
      <p className="sx-body sx-sm">{value}</p>
    </>
  );
}

function timestamp(value: string | null): string | null {
  if (value === null) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function CuratorQueueRow({
  row,
  disabled,
  busy,
  error,
  conflicted,
  blockReason,
  refreshing,
  onDecide,
  onRefresh,
}: {
  row: QueueRow;
  /** True while ANY row's decision is in flight: the queue shares one mutation. */
  disabled: boolean;
  /** True only for the row actually being decided. */
  busy: boolean;
  error: CuratorApiError | null;
  conflicted: boolean;
  /** Why submission is refused for THIS row, or null. */
  blockReason: SubmissionBlock | null;
  refreshing: boolean;
  onDecide: (kind: DecisionKind, reason: string | null) => void;
  onRefresh: () => void;
}): JSX.Element {
  const [choice, setChoice] = useState<DecisionKind | null>(null);
  const observed = timestamp(row.firstObservedAt ?? row.observedAt);
  const lastSeen = timestamp(row.lastObservedAt);

  return (
    <li className="sx-z-activation" style={{ marginBottom: 12 }}>
      <p className="sx-zone-title sx-sm">
        {row.title ?? '(no title on record)'}
      </p>
      <p className="sx-helper">
        {row.organizationName ?? 'No organization resolved'}
        {' · '}
        {PUBLICATION_STATE_LABELS[row.publicationState]}
        {' · version '}
        {row.publicationVersion}
      </p>

      <Field label="Summary" value={row.summary} />
      <Field label="Why it matters" value={row.whyItMatters} />
      <Field label="Recommended action" value={row.recommendedAction} />
      <Field label="Talk track" value={row.talkTrack} />
      <Field label="Signal type" value={row.signalType} />
      <Field label="Source system" value={row.sourceSystem} />
      <Field label="First observed" value={observed} />
      <Field label="Last observed" value={lastSeen} />
      <Field
        label="Times observed"
        value={row.observedCount === null ? null : String(row.observedCount)}
      />
      <Field label="Source event" value={row.sourceEventId} />

      {row.sourceUrl ? (
        <p className="sx-body sx-sm">
          <a href={row.sourceUrl} target="_blank" rel="noreferrer noopener">
            Open source link
          </a>
        </p>
      ) : (
        <p className="sx-helper">No usable source link on record.</p>
      )}

      {row.latestDecision ? (
        <p className="sx-helper">
          Last decision:{' '}
          {row.latestDecision.kind
            ? DECISION_LABELS[row.latestDecision.kind]
            : 'recorded'}
          {row.latestDecision.actorDisplayName
            ? ` by ${row.latestDecision.actorDisplayName}`
            : ''}
          {timestamp(row.latestDecision.decidedAt)
            ? ` on ${timestamp(row.latestDecision.decidedAt)}`
            : ''}
          {row.latestDecision.reason ? ` — ${row.latestDecision.reason}` : ''}
        </p>
      ) : null}

      <p className="sx-helper">
        Signal and source context only. Retained source evidence is not shown
        here.
      </p>

      <div className="sx-outreach-list">
        {decisionsForState(row.publicationState).map((kind) => (
          <button
            key={kind}
            type="button"
            className="sx-btn sx-btn-p2"
            aria-haspopup="dialog"
            aria-expanded={choice === kind}
            disabled={disabled}
            onClick={() => setChoice(choice === kind ? null : kind)}
          >
            {DECISION_LABELS[kind]}
          </button>
        ))}
      </div>

      {choice ? (
        <CuratorDecisionConfirm
          kind={choice}
          disabled={disabled}
          busy={busy}
          error={error}
          conflicted={conflicted}
          blockReason={blockReason}
          refreshing={refreshing}
          onCancel={() => setChoice(null)}
          onConfirm={(reason) => onDecide(choice, reason)}
          onRefresh={onRefresh}
        />
      ) : null}
    </li>
  );
}
